import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'

import { classNames } from '../../helpers'
import { LockPill } from '../pricing/lock-badge'

// Gesperrte Optionen (lockedOptionIds) bleiben sichtbar und anklickbar, zeigen
// eine Schloss-Pille und ändern den Wert nicht. Stattdessen geht die ID an
// onLockedSelect, der Aufrufer zeigt dann etwa UpgradeInline. Die gewählte
// Option gilt nie als gesperrt. Ohne lockedOptionIds verhält sich das Menü
// wie bisher. Wie FeatureLockPill in SelectMenu liest der Screenreader an der
// Pille lockedAriaLabel, lockedTitle ist der Tooltip.
const SelectMenuWithDescription = ({
  label,
  selected,
  setSelected,
  options,
  disabled,
  lockedOptionIds,
  onLockedSelect,
  lockedLabel,
  lockedAriaLabel,
  lockedTitle
}) => {
  const isLocked = (option) =>
    !!option &&
    option.id !== selected?.id &&
    lockedOptionIds.includes(option.id)

  const handleChange = (option) => {
    if (isLocked(option)) {
      onLockedSelect?.(option.id)
      return
    }

    setSelected(option)
  }

  return (
    <>
      <label htmlFor='role' className='block text-sm font-medium text-gray-700'>
        {label}
      </label>
      <div className='mt-2' />
      <Listbox value={selected} onChange={handleChange} disabled={disabled}>
        <div className='relative'>
          <ListboxButton
            className={classNames(
              'group w-full',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            <div className='flex divide-x divide-gray-300 rounded-md border border-gray-300 shadow-sm'>
              <div className='inline-flex w-full items-center gap-x-1.5 rounded-l-md bg-white px-3 py-2 shadow-sm'>
                <CheckIcon
                  className='-ml-0.5 h-5 w-5 text-gray-700'
                  aria-hidden='true'
                />
                <p className='text-sm text-gray-900'>{selected.title}</p>
              </div>
              <div className='inline-flex items-center rounded-l-none rounded-r-md bg-white p-2 focus:outline-none focus:ring-0 focus:ring-offset-0 group-hover:bg-gray-50'>
                <ChevronDownIcon
                  className='h-5 w-5 text-gray-700'
                  aria-hidden='true'
                />
              </div>
            </div>
          </ListboxButton>
          <ListboxOptions className='absolute right-0 z-10 mt-2 w-full origin-top-right divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
            {options.map((option) => {
              const active = option.id === selected.id
              const locked = isLocked(option)

              if (locked) {
                return (
                  <ListboxOption
                    key={option.id}
                    className='group cursor-pointer select-none p-4 text-sm text-gray-900 hover:bg-gray-50 data-[focus]:bg-gray-50'
                    value={option}
                    data-locked=''
                  >
                    <div className='flex flex-col'>
                      <div className='flex items-start justify-between gap-x-3'>
                        <p className='font-normal'>{option.title}</p>
                        <span title={lockedTitle} className='inline-flex'>
                          <LockPill label={lockedLabel} />
                          {lockedAriaLabel && (
                            <span className='sr-only'>{lockedAriaLabel}</span>
                          )}
                        </span>
                      </div>
                      <p className='mt-2 text-gray-500'>{option.description}</p>
                    </div>
                  </ListboxOption>
                )
              }

              return (
                <ListboxOption
                  key={option.id}
                  className={classNames(
                    active
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-900 hover:bg-indigo-600 hover:text-white',
                    'group cursor-pointer select-none p-4 text-sm'
                  )}
                  value={option}
                >
                  <div className='flex flex-col'>
                    <div className='flex justify-between'>
                      <p
                        className={
                          active
                            ? 'font-semibold'
                            : 'font-normal group-hover:font-semibold'
                        }
                      >
                        {option.title}
                      </p>
                      {active ? (
                        <span className='text-white'>
                          <CheckIcon className='h-5 w-5' aria-hidden='true' />
                        </span>
                      ) : null}
                    </div>
                    <p
                      className={classNames(
                        active
                          ? 'text-indigo-200'
                          : 'text-gray-500 group-hover:text-indigo-200',
                        'mt-2'
                      )}
                    >
                      {option.description}
                    </p>
                  </div>
                </ListboxOption>
              )
            })}
          </ListboxOptions>
        </div>
      </Listbox>
    </>
  )
}

SelectMenuWithDescription.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.object.isRequired,
  setSelected: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  lockedOptionIds: PropTypes.arrayOf(PropTypes.string),
  onLockedSelect: PropTypes.func,
  lockedLabel: PropTypes.node,
  lockedAriaLabel: PropTypes.string,
  lockedTitle: PropTypes.string
}

SelectMenuWithDescription.defaultProps = {
  disabled: false,
  lockedOptionIds: [],
  onLockedSelect: undefined,
  lockedLabel: null,
  lockedAriaLabel: undefined,
  lockedTitle: undefined
}

export default SelectMenuWithDescription
